import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PropertyCard } from "@/components/home/PropertyCard";
import { api } from "@/lib/api";
import { dictionary } from "@/i18n/translations";
import { setDocumentMeta } from "@/lib/seo";

const AMENITY_OPTIONS = [
  "pool",
  "garden",
  "sea-view",
  "smart-home",
  "gym",
  "majlis",
  "security",
  "parking",
];

const parseNumber = (value) => {
  if (!value) {
    return "";
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? "" : String(parsed);
};

const collectAvailablePropertyTypes = (items, labels) =>
  Array.from(
    new Set(
      (Array.isArray(items) ? items : [])
        .map((item) => item?.property_type)
        .filter((key) => key && labels?.[key]),
    ),
  );

export default function AdvancedSearchPage({ lang }) {
  const copy = dictionary[lang];
  const [searchParams, setSearchParams] = useSearchParams();

  const [subAreas, setSubAreas] = useState([]);
  const [loadingSubAreas, setLoadingSubAreas] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [availablePropertyTypes, setAvailablePropertyTypes] = useState([]);

  const formState = useMemo(
    () => ({
      region: searchParams.get("region") || "makkah",
      subArea: searchParams.get("subArea") || "all",
      listingType: searchParams.get("listingType") || "all",
      propertyType: searchParams.get("propertyType") || "all",
      minPrice: parseNumber(searchParams.get("minPrice")),
      maxPrice: parseNumber(searchParams.get("maxPrice")),
      bedrooms: parseNumber(searchParams.get("bedrooms")),
      bathrooms: parseNumber(searchParams.get("bathrooms")),
      minArea: parseNumber(searchParams.get("minArea")),
      amenities: (searchParams.get("amenities") || "").split(",").filter(Boolean),
    }),
    [searchParams],
  );

  const propertyTypeLabels = copy.advancedSearch.propertyTypes;

  const buildPropertiesQuery = useCallback(
    ({ includePropertyType = true } = {}) => {
      const query = new URLSearchParams();
      query.set("lang", lang);

      if (formState.region !== "all") {
        query.set("region", formState.region);
      }
      if (formState.subArea !== "all") {
        query.set("sub_area", formState.subArea);
      }
      if (formState.listingType !== "all") {
        query.set("listing_type", formState.listingType);
      }
      if (includePropertyType && formState.propertyType !== "all") {
        query.set("property_type", formState.propertyType);
      }
      if (formState.minPrice) {
        query.set("min_price", formState.minPrice);
      }
      if (formState.maxPrice) {
        query.set("max_price", formState.maxPrice);
      }
      if (formState.bedrooms) {
        query.set("min_bedrooms", formState.bedrooms);
      }
      if (formState.bathrooms) {
        query.set("min_bathrooms", formState.bathrooms);
      }
      if (formState.minArea) {
        query.set("min_area", formState.minArea);
      }
      if (formState.amenities.length) {
        query.set("amenities", formState.amenities.join(","));
      }

      return query.toString();
    },
    [
      formState.amenities,
      formState.bathrooms,
      formState.bedrooms,
      formState.listingType,
      formState.maxPrice,
      formState.minArea,
      formState.minPrice,
      formState.propertyType,
      formState.region,
      formState.subArea,
      lang,
    ],
  );

  useEffect(() => {
    setDocumentMeta({
      lang,
      title: `${copy.footer.quickLinks.advancedSearch} | ${copy.nav.brand}`,
      description: copy.advancedSearch.subtitle,
    });
  }, [copy.advancedSearch.subtitle, copy.footer.quickLinks.advancedSearch, copy.nav.brand, lang]);

  useEffect(() => {
    const fetchSubAreas = async () => {
      if (!formState.region || formState.region === "all") {
        setSubAreas([]);
        return;
      }

      setLoadingSubAreas(true);
      try {
        const response = await api.get(`/regions/${formState.region}/sub-areas`);
        setSubAreas(Array.isArray(response.data) ? response.data : []);
      } catch {
        setSubAreas([]);
      } finally {
        setLoadingSubAreas(false);
      }
    };

    fetchSubAreas();
  }, [formState.region]);

  useEffect(() => {
    const fetchResults = async () => {
      setResultsLoading(true);
      try {
        const response = await api.get(`/properties?${buildPropertiesQuery()}`);
        setProperties(Array.isArray(response.data) ? response.data : []);
      } catch {
        toast.error(copy.common.requestFailed);
      } finally {
        setResultsLoading(false);
      }
    };

    fetchResults();
  }, [buildPropertiesQuery, copy.common.requestFailed]);

  useEffect(() => {
    if (formState.propertyType === "all") {
      setAvailablePropertyTypes(collectAvailablePropertyTypes(properties, propertyTypeLabels));
      return undefined;
    }

    let cancelled = false;

    const fetchAvailableTypes = async () => {
      try {
        const response = await api.get(`/properties?${buildPropertiesQuery({ includePropertyType: false })}`);
        if (cancelled) {
          return;
        }

        const nextTypes = new Set(collectAvailablePropertyTypes(response.data, propertyTypeLabels));
        if (propertyTypeLabels[formState.propertyType]) {
          nextTypes.add(formState.propertyType);
        }
        setAvailablePropertyTypes(Array.from(nextTypes));
      } catch {
        if (cancelled) {
          return;
        }

        const fallbackTypes = new Set(collectAvailablePropertyTypes(properties, propertyTypeLabels));
        if (propertyTypeLabels[formState.propertyType]) {
          fallbackTypes.add(formState.propertyType);
        }
        setAvailablePropertyTypes(Array.from(fallbackTypes));
      }
    };

    fetchAvailableTypes();

    return () => {
      cancelled = true;
    };
  }, [buildPropertiesQuery, formState.propertyType, properties, propertyTypeLabels]);

  const updateQuery = (next) => {
    const params = new URLSearchParams(searchParams.toString());
    Object.entries(next).forEach(([key, value]) => {
      const text = Array.isArray(value) ? value.join(",") : String(value ?? "").trim();
      if (!text || text === "all") {
        params.delete(key);
      } else {
        params.set(key, text);
      }
    });

    setSearchParams(params);
  };

  const resolvedSubAreas = (Array.isArray(subAreas) ? subAreas : [])
    .filter((item) => item && typeof item === "object")
    .map((item) => ({
      ...item,
      label: item.name?.[lang] || item.name?.ar || item.code,
    }));

  const propertyTypeChips = useMemo(() => {
    const visibleTypes = new Set(availablePropertyTypes);
    const orderedTypes = Object.entries(propertyTypeLabels).filter(
      ([key]) => visibleTypes.has(key) || key === formState.propertyType,
    );

    return [
      { key: "all", label: copy.advancedSearch.allLabel },
      ...orderedTypes.map(([key, label]) => ({ key, label })),
    ];
  }, [availablePropertyTypes, copy.advancedSearch.allLabel, formState.propertyType, propertyTypeLabels]);

  const toggleAmenity = (amenity) => {
    const exists = formState.amenities.includes(amenity);
    const nextAmenities = exists
      ? formState.amenities.filter((item) => item !== amenity)
      : [...formState.amenities, amenity];
    updateQuery({ amenities: nextAmenities });
  };

  return (
    <div className="min-h-screen bg-luxury-surface pt-28" data-testid="advanced-search-page-root">
      <main className="mx-auto max-w-7xl space-y-8 px-4 pb-24 sm:px-10" data-testid="advanced-search-page-main">
        <section className="space-y-2" data-testid="advanced-search-header">
          <h1 className="text-3xl font-semibold text-stone-900 sm:text-4xl" data-testid="advanced-search-title">
            {copy.footer.quickLinks.advancedSearch}
          </h1>
          <p className="text-sm text-stone-600" data-testid="advanced-search-subtitle">
            {copy.advancedSearch.subtitle}
          </p>
        </section>

        <section className="space-y-4" data-testid="advanced-search-toolbar-section">
          <div className="advanced-property-type-strip-shell" data-testid="advanced-search-type-strip-shell">
            <div className="advanced-property-type-strip-header">
              <p className="advanced-property-type-strip-title" data-testid="advanced-search-type-strip-title">
                {copy.advancedSearch.propertyTypeStripTitle}
              </p>
            </div>
            <div className="advanced-property-type-strip" data-testid="advanced-search-type-strip">
              {propertyTypeChips.map((chip) => {
                const active = formState.propertyType === chip.key || (chip.key === "all" && formState.propertyType === "all");
                return (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => updateQuery({ propertyType: chip.key })}
                    className={`advanced-property-type-chip ${active ? "active" : ""}`}
                    data-testid={`advanced-search-type-chip-${chip.key}`}
                  >
                    <span className="advanced-property-type-chip-dot" aria-hidden="true" />
                    <span>{chip.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <Card className="border-gold-500/20 bg-white/90" data-testid="advanced-search-filters-card">
            <CardHeader className={filtersOpen ? "pb-3" : "pb-0"}>
              <button
                type="button"
                className="advanced-search-toggle-button"
                onClick={() => setFiltersOpen((prev) => !prev)}
                data-testid="advanced-search-filters-toggle-button"
                aria-expanded={filtersOpen}
                aria-label={copy.advancedSearch.filtersTitle}
              >
                <div className="advanced-search-toggle-copy">
                  <CardTitle data-testid="advanced-search-filters-title">{copy.advancedSearch.filtersTitle}</CardTitle>
                  <p className="advanced-search-toggle-meta" data-testid="advanced-search-filters-toggle-meta">
                    {filtersOpen ? copy.advancedSearch.hideFilters : copy.advancedSearch.showFilters}
                  </p>
                </div>
                <ChevronDown
                  size={18}
                  className={`advanced-search-toggle-icon ${filtersOpen ? "is-open" : ""}`}
                  aria-hidden="true"
                />
              </button>
            </CardHeader>

            <CardContent
              className={`${filtersOpen ? "grid" : "hidden"} gap-3 md:grid-cols-2 xl:grid-cols-4`}
              data-testid="advanced-search-filters-grid"
            >
              <Select value={formState.region} onValueChange={(value) => updateQuery({ region: value, subArea: "all" })}>
                <SelectTrigger data-testid="advanced-search-region-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="advanced-search-region-all">{copy.advancedSearch.allRegions}</SelectItem>
                  {Object.entries(copy.regions)
                    .filter(([key]) => ["makkah", "riyadh", "eastern", "madinah", "qassim", "asir", "tabuk", "jazan"].includes(key))
                    .map(([key, label]) => (
                      <SelectItem value={key} key={key} data-testid={`advanced-search-region-${key}`}>{label}</SelectItem>
                    ))}
                </SelectContent>
              </Select>

              <Select value={formState.subArea} onValueChange={(value) => updateQuery({ subArea: value })}>
                <SelectTrigger data-testid="advanced-search-subarea-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="advanced-search-subarea-all">{copy.advancedSearch.allSubAreas}</SelectItem>
                  {resolvedSubAreas.map((item) => (
                    <SelectItem key={item.code} value={item.code} data-testid={`advanced-search-subarea-${item.code}`}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={formState.listingType} onValueChange={(value) => updateQuery({ listingType: value })}>
                <SelectTrigger data-testid="advanced-search-listing-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="advanced-search-listing-all">{copy.advancedSearch.anyListing}</SelectItem>
                  <SelectItem value="sale" data-testid="advanced-search-listing-sale">{copy.properties.sale}</SelectItem>
                  <SelectItem value="rent" data-testid="advanced-search-listing-rent">{copy.properties.rent}</SelectItem>
                </SelectContent>
              </Select>

              <Select value={formState.propertyType} onValueChange={(value) => updateQuery({ propertyType: value })}>
                <SelectTrigger data-testid="advanced-search-property-type-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="advanced-search-property-type-all">{copy.advancedSearch.anyPropertyType}</SelectItem>
                  {Object.entries(propertyTypeLabels).map(([key, label]) => (
                    <SelectItem value={key} key={key} data-testid={`advanced-search-property-type-${key}`}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input type="number" min="0" value={formState.minPrice} onChange={(event) => updateQuery({ minPrice: event.target.value })} placeholder={copy.advancedSearch.minPrice} data-testid="advanced-search-min-price-input" />
              <Input type="number" min="0" value={formState.maxPrice} onChange={(event) => updateQuery({ maxPrice: event.target.value })} placeholder={copy.advancedSearch.maxPrice} data-testid="advanced-search-max-price-input" />
              <Input type="number" min="0" value={formState.bedrooms} onChange={(event) => updateQuery({ bedrooms: event.target.value })} placeholder={copy.properties.bedrooms} data-testid="advanced-search-bedrooms-input" />
              <Input type="number" min="0" value={formState.bathrooms} onChange={(event) => updateQuery({ bathrooms: event.target.value })} placeholder={copy.properties.bathrooms} data-testid="advanced-search-bathrooms-input" />
              <Input type="number" min="0" value={formState.minArea} onChange={(event) => updateQuery({ minArea: event.target.value })} placeholder={copy.advancedSearch.minArea} data-testid="advanced-search-min-area-input" />

              <div className="md:col-span-2 xl:col-span-3" data-testid="advanced-search-amenities-section">
                <p className="mb-2 text-xs text-stone-600" data-testid="advanced-search-amenities-title">{copy.advancedSearch.amenities}</p>
                <div className="flex flex-wrap gap-2" data-testid="advanced-search-amenities-list">
                  {AMENITY_OPTIONS.map((amenity) => {
                    const active = formState.amenities.includes(amenity);
                    return (
                      <button
                        key={amenity}
                        type="button"
                        onClick={() => toggleAmenity(amenity)}
                        className={`advanced-amenity-chip ${active ? "advanced-amenity-chip-active" : ""}`}
                        data-testid={`advanced-search-amenity-${amenity}`}
                      >
                        {copy.advancedSearch.amenityLabels[amenity] || amenity}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-2 xl:col-span-1" data-testid="advanced-search-actions-row">
                <Button
                  onClick={() => setSearchParams(new URLSearchParams(searchParams.toString()))}
                  data-testid="advanced-search-apply-button"
                >
                  {copy.advancedSearch.apply}
                </Button>
                <Button variant="outline" onClick={() => setSearchParams(new URLSearchParams())} data-testid="advanced-search-reset-button">
                  {copy.advancedSearch.reset}
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="space-y-4" data-testid="advanced-search-results-section">
          <p className="text-sm text-stone-600" data-testid="advanced-search-results-count">
            {resultsLoading ? copy.common.loading : `${properties.length} ${copy.advancedSearch.resultsLabel}`}
          </p>

          {loadingSubAreas && <p className="text-xs text-stone-500" data-testid="advanced-search-subarea-loading">{copy.regions.loadingMap}</p>}

          {!resultsLoading && properties.length === 0 && (
            <p className="rounded-2xl border border-dashed border-gold-500/30 bg-white/70 p-6 text-sm text-stone-600" data-testid="advanced-search-empty-state">
              {copy.advancedSearch.noResults}
            </p>
          )}

          {!resultsLoading && properties.length > 0 && (
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3" data-testid="advanced-search-results-grid">
              {properties.map((property) => (
                <PropertyCard key={property.id} lang={lang} labels={copy.properties} property={property} />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
